import { useState, useRef, useEffect, FormEvent, useMemo } from 'react';
import { WarnText } from '../atoms';
import { MessageCodeOption, MessageOwnerOption, useCreateNewMessageFromIncidentMutation, useGetMessagesByIncidentQuery, useOnNewMessageInIncidentSubscription, UserTypeOption } from '@/graphql/generated';
import { LoadingSpinner } from '../atoms/LoadingSpinner';
import { ulid } from 'ulid';
import { useUserLanguage } from '@/customHooks';

const textsData = {
  en: {
    sendButton: 'Send',
    relativeDay: {
      today: 'Today',
      yesterday: 'Yesterday',
    },
    error: {
      message1: 'Message Not Send, problem with server',
      message2: 'Message Not Send, receiver not found',
      message3: 'Chat conection error, please try again',
    },
  },
  es: {
    sendButton: 'Enviar',
    relativeDay: {
      today: 'Hoy',
      yesterday: 'Ayer',
    },
    error: {
      message1: 'Mensaje no enviado, prolemas con el servidor',
      message2: 'Mensaje no enviado, no se encontró receptor',
      message3: 'Conexión al chat falló. Intente de nuevo',
    },
  }
}

export type Message = {
  id: string
  text: string
  timestamp: Date
  sender?: MessageOwnerOption | null
  senderUserTypeOption?: UserTypeOption | null
  receiverUserTypeOption?: UserTypeOption | null
  messageTransactionId?: string | null
  isError?: boolean;
};

type ChatWindowProps = {
  incidentId: string;
};

export const ChatWindow = ({ incidentId }: ChatWindowProps) => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es : textsData.en

  const [createMessage, { loading: isSending }] = useCreateNewMessageFromIncidentMutation();
  const { data: subscriptionData, error: subscriptionError } = useOnNewMessageInIncidentSubscription({
    variables: { incidentId },
  });

  const { data: initialData, loading: isLoadingInitial, error: initialError } = useGetMessagesByIncidentQuery({
    variables: { incidentId },
    fetchPolicy: 'network-only',
  });

  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [activeTab, setActiveTab] = useState<UserTypeOption>(UserTypeOption.Tenant);
  const [lastMessageTransactionId, setLastMessageTransactionId] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (initialData?.getMessagesByIncident !== undefined) {
      const processedMessages = initialData.getMessagesByIncident.map(msg => ({
        id: msg.id,
        text: msg.content,
        timestamp: new Date(msg.createdAt),
        sender: msg.messageOwnerOption,
        senderUserTypeOption: msg.senderUserTypeOption,
        receiverUserTypeOption: msg.receiverUserTypeOption,
      })).sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
      setMessages(processedMessages);
    }
  }, [initialData]);

  useEffect(() => {
    if (subscriptionData?.newMessageInIncident) {
      const newMsg = subscriptionData.newMessageInIncident;

      if (messages.some(m => m.id === newMsg.id)) return

      if (newMsg.messageTransactionId === undefined || newMsg.messageTransactionId === null) return

      if (newMsg.messageTransactionId === lastMessageTransactionId) {
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            id: newMsg.id,
            text: newMsg.content,
            timestamp: new Date(newMsg.createdAt),
            sender: MessageOwnerOption.Me,
            senderUserTypeOption: newMsg.senderUserTypeOption,
            receiverUserTypeOption: newMsg.receiverUserTypeOption,
          },
        ]);
      } else {
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            id: newMsg.id,
            text: newMsg.content,
            timestamp: new Date(newMsg.createdAt),
            sender: MessageOwnerOption.Other,
            senderUserTypeOption: newMsg.senderUserTypeOption,
            receiverUserTypeOption: newMsg.receiverUserTypeOption,
          },
        ]);
      }
    }
  }, [subscriptionData, messages, lastMessageTransactionId]);

  const filteredMessages = useMemo(() => {
    return messages.filter(msg => {
      if (msg.isError) {
        return true;
      }
      const sender = msg.senderUserTypeOption;
      const receiver = msg.receiverUserTypeOption;
      const manager = UserTypeOption.Manager;

      if (activeTab === UserTypeOption.Tenant) {
        const isManagerToTenant = sender === manager && receiver === UserTypeOption.Tenant;
        const isTenantToManager = sender === UserTypeOption.Tenant && receiver === manager;
        return isManagerToTenant || isTenantToManager;
      }

      if (activeTab === UserTypeOption.Supplier) {
        const isManagerToSupplier = sender === manager && receiver === UserTypeOption.Supplier;
        const isSupplierToManager = sender === UserTypeOption.Supplier && receiver === manager;
        return isManagerToSupplier || isSupplierToManager;
      }

      return false;
    });
  }, [messages, activeTab]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const locales = userLanguage === 'es' ? 'es-ES' : 'en-US'

  const formatTimeInBubble = (date: Date) =>
    date.toLocaleTimeString(locales, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })

  const timelineItems = useMemo(() => {
    const rel = texts.relativeDay
    const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate())
    const labelForDay = (date: Date): string => {
      const msgDay = startOfDay(date)
      const todayDay = startOfDay(new Date())
      const diffDays = Math.round((todayDay.getTime() - msgDay.getTime()) / 86_400_000)
      if (diffDays === 0) return rel.today
      if (diffDays === 1) return rel.yesterday
      const currentYear = new Date().getFullYear()
      return date.toLocaleDateString(locales, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: date.getFullYear() !== currentYear ? 'numeric' : undefined,
      })
    }

    const items: Array<
      | { kind: 'sep'; key: string; label: string }
      | { kind: 'msg'; msg: Message }
    > = []
    let prevDayKey = ''

    for (const msg of filteredMessages) {
      const d = msg.timestamp
      const dayKey = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
      if (dayKey !== prevDayKey) {
        prevDayKey = dayKey
        items.push({
          kind: 'sep',
          key: dayKey,
          label: labelForDay(d),
        })
      }
      items.push({ kind: 'msg', msg })
    }
    return items
  }, [filteredMessages, texts.relativeDay, locales])

  const handleSendMessage = (e: FormEvent) => {
    e.preventDefault();
    if (newMessage.trim() === '') return;

    setNewMessage('');

    const messageTransactionId = ulid()

    setLastMessageTransactionId(messageTransactionId)

    createMessage({
      variables: {
        incidentId,
        input: {
          content: newMessage,
          messageTransactionId,
          userTypeOptionDestiny: activeTab
        },

      },
    })
      .then(response => {
        const result = response.data?.createMessageFromIncident

        if (!result) return

        if (result.__typename === 'CreateMessageOutput' && result.messageSent) {
          console.log('Message has been sent')
        } else if (result.__typename === 'ActionResponseMessageOutput') {
          let errorMessage = 'An unknown error occurred.';
          if (result.code === MessageCodeOption.ChatMessageNotDeliveredByInternalFail) {
            errorMessage = texts.error.message1;
          } else if (result.code === MessageCodeOption.ChatMessageNotDeliveredByRelationNotFound) {
            errorMessage = texts.error.message2;
          }

          setMessages((prevMessages) => [
            ...prevMessages,
            {
              id: ulid(),
              text: errorMessage,
              timestamp: new Date(),
              sender: MessageOwnerOption.Me,
              isError: true,
            },
          ]);
          console.warn(`Message not sent: ${result.message}`);
        }
      }).catch(error => {
        console.error("Error al enviar el mensaje:", error);
      });
  };

  if (isLoadingInitial) {
    return (
      <div className="flex h-full min-h-[280px] w-full flex-col overflow-hidden rounded-xl bg-background2">
        <div className="flex flex-1 items-center justify-center">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (initialError) return <p className="p-4 text-sm">Error al cargar el chat. Inténtalo de nuevo.</p>;

  if (subscriptionError) console.error(subscriptionError)

  return (
    <div className="flex h-full min-h-0 w-full max-w-[600px] flex-col overflow-hidden rounded-xl bg-background2">
      <div className="shrink-0 bg-primary2 text-white">
        <div className="flex">
          <button
            type="button"
            onClick={() => setActiveTab(UserTypeOption.Tenant)}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === UserTypeOption.Tenant
                ? 'border-b-2 border-white text-white'
                : 'border-b-2 border-transparent text-white/75 hover:text-white'
            }`}
          >
            Inquilino
          </button>
          <button
            type="button"
            onClick={() => setActiveTab(UserTypeOption.Supplier)}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === UserTypeOption.Supplier
                ? 'border-b-2 border-white text-white'
                : 'border-b-2 border-transparent text-white/75 hover:text-white'
            }`}
          >
            Proveedor
          </button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto bg-background2 p-4">
        {timelineItems.map((item) => {
          if (item.kind === 'sep') {
            return (
              <div
                key={`sep-${item.key}`}
                className="flex w-full shrink-0 justify-center py-2"
              >
                <span className="rounded-full bg-white/70 px-3 py-1 text-center text-xs font-bold text-[#444] shadow-sm backdrop-blur-[1px]">
                  {item.label}
                </span>
              </div>
            )
          }

          const msg = item.msg
          const isMe = msg.sender === MessageOwnerOption.Me
          const isErr = msg.isError
          return (
            <div
              key={msg.id}
              className={`flex w-full ${isMe ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[75%] rounded-xl px-4 py-3 shadow-sm ${
                  isErr
                    ? 'bg-red-400 text-white'
                    : isMe
                      ? 'border border-gray-200 bg-white text-[var(--color-text)]'
                      : 'border border-teal-100/80 bg-[#e8f6f3] text-[var(--color-text)]'
                }`}
              >
                <p className="m-0 break-words text-sm">{msg.text}</p>
                <span
                  className={`mt-1 block text-right text-xs opacity-90 ${
                    isErr ? 'text-white/90' : 'text-[#666]'
                  }`}
                >
                  {formatTimeInBubble(msg.timestamp)}
                </span>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {subscriptionError ? (
        <div className="flex justify-center px-2 pb-2">
          <WarnText
            text={texts.error.message3}
          />
        </div>
      ) : null}

      <form
        onSubmit={handleSendMessage}
        className="flex border-t border-gray-200 bg-white p-4"
      >
        <input
          type="text"
          placeholder="Escribe un mensaje..."
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          className="mr-2 min-w-0 flex-1 rounded-md border border-gray-300 px-3 py-2.5 text-sm text-[var(--color-text)] placeholder:text-gray-400 focus:border-primary2 focus:outline-none focus:ring-1 focus:ring-primary2"
        />
        <button
          type="submit"
          disabled={!!subscriptionError || isSending}
          className="shrink-0 rounded-[58px] border-2 border-primary2 bg-primary2 px-5 py-2.5 text-sm font-medium text-white shadow-[0px_4px_4px_#00000017] transition hover:opacity-90 disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-300"
        >
          {isSending ? <LoadingSpinner sm color="white" /> : texts.sendButton}
        </button>
      </form>
    </div>
  );
};
